import { createContext, useContext } from "react";

import dayjs from "dayjs";
import { CurrencyType, IImportedCommitment, UiMeasureDataFiles } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

export interface Group {
  id: number;
  original_measure_id: number | null; // if measure_id is changed, we need to store the old measure_id
  original_group_measure_data_ids: number[]; // existing measure data before change
  delete_measure_data_ids: number[]; // if measure_id is changed, push measure data ids to delete
  total_amount: number; //if measure_id is changed, we need to store the total amount? and pass to total for new measure
  // deal_id: number | null;
  deal_id: number;
  sub_deal_id: number | null;
  measure_id: number | null;
  files: Record<string | number, UiMeasureDataFiles> | null;
}

export interface MeasureData {
  key: number;
  group_id: number;
  value: string;
  investor_id: number | null;
  investor_name: string | null;
  description: string | null;
  round_id: number | string | null;
  // round_name?: string | null;
  date: string | null;
  accounting_date: string | null;
  currency: CurrencyType | null;
  enable_split: boolean;
  split_id?: number;
  split_data_details_id?: number;
  batch_id?: number;
  id?: number;
  isDeletable?: boolean;
  effective_range_from: string | null;
  effective_range_to: string | null;
  tag_ids?: number[]; //we have it when user saved draft from tagger
  files: UiMeasureDataFiles | null;
  custom_payment_id?: number;
  custom_payment_type?:
    | "principal_measure_data_id"
    | "interest_measure_data_id"
    | "pik_interest_measure_data_id"
    | "vat_measure_data_id"
    | "principal_indexation_measure_data_id"
    | "fee_measure_data_id";

  investor_group: string | null;
}

export interface ImportedMeasureData {
  value: string;
  investor_id: number;
  investor_name: string;
  round_id: number;
  date: string;
}

export interface GroupFormDealData {
  deal_id: number;
  subDeals: { title: string; id: number }[];
  parentId: number | null;
  defaultCurrency: CurrencyType;
}

export type MeasureDataRound = {
  investors: { id: number; name: string; enable_split: boolean; investor_group: string | null }[];
  id: number | string;
  name: string;
  is_deleted: boolean;
};

export type MeasureDataGroup = { data: MeasureData[] } & Group;

export type MeasureDataByRound = {
  round_id: number | string;
  title: string;
  measureData: MeasureData[];
};

export type MeasureDataErrors = {
  value?: string[];
  accounting_date?: string[];
  date?: string[];
  entity?: string[];
  investor_id?: string[];
};

export type RoundsByDealsErrors = Record<number, Record<string | number, string[]>>;

export const getDefaultGroup = (data: Partial<Omit<Group, "id">> & Required<Pick<Group, "id" | "deal_id">>): Group => ({
  sub_deal_id: null,
  measure_id: null,
  total_amount: 0,
  original_measure_id: null,
  delete_measure_data_ids: [],
  original_group_measure_data_ids: [],
  files: null,
  ...data
});

export const getDefaultMeasureData = (
  data: Partial<Omit<MeasureData, "key" | "group_id">> & Required<Pick<MeasureData, "key" | "group_id">>
): MeasureData => ({
  value: "",
  investor_id: null,
  investor_name: null,
  description: null,
  round_id: null,
  // round_name: null,
  date: toIsoMidday(dayjs().toISOString()),
  accounting_date: null,
  currency: null,
  enable_split: false,
  effective_range_from: null,
  effective_range_to: null,
  files: null,
  investor_group: null,
  ...data
});

export interface CommentPopup {
  anchorEl: HTMLElement;
  measureDataKey: number;
  initialValue: string;
}

export type UpdateMeasurePayloadPayload = Partial<
  Pick<
    MeasureData,
    | "currency"
    | "date"
    | "accounting_date"
    | "value"
    | "description"
    | "investor_id"
    | "investor_name"
    | "effective_range_from"
    | "effective_range_to"
  >
>;

export enum Errors {
  SAME_INVESTOR_PER_ROUND = "The same investor more than once",
  COULD_NOT_FIND_GROUP = "Couldn't fund the group to work with",
  MORE_THAN_ONE_INVESTOR = "There are more then one investor",
  NO_GROUP_FOUND = "There is no group with id = ",
  SOMETHING_WENT_WRONG = "Something went wrong",
  CANCEL_CONFIRMATION = "Cancel confirmation",
  VALIDATION_FAILED = "Validation failed",
  NO_FROM_DATE = "No from date",
  NO_TO_DATE = "No to date",
  NO_SOURCE = "No source"
}

export interface IGroupsFormContext {
  createGroup: () => void;
  updateGroup: (data: Partial<Omit<Group, "id" | "deal_id">> & Required<Pick<Group, "id">>) => void;
  deleteGroup: (id: number) => void;
  importCommitments: (data: IImportedCommitment[]) => Promise<void>;
  createMeasureData: (data: {
    round_id: number | string;
    group_id: number;
    investor_name: string;
    investor_id: number;
    investor_group?: string | null;
    value?: string;
    date?: string;
    currency?: CurrencyType | null;
    accounting_date?: string | null;
    effective_range_from?: string | null;
    effective_range_to?: string | null;
  }) => void;
  deleteMeasureData: (key: number) => void;
  selectFromMeasureData: (where: Pick<MeasureData, "group_id">) => MeasureData[];
  updateMeasureData: (data: Pick<MeasureData, "key"> & UpdateMeasurePayloadPayload) => void;
  updateGroupMeasureData: (
    round_id: number | string,
    investors: { id: number; value: string }[],
    group_id: number
  ) => void;
  updateManyMeasureData: (elementKeys: number[], data: UpdateMeasurePayloadPayload) => void;
  calculateMeasureData: (data: {
    group_id: number;
    round_id: number;
    value: string;
    investor_id?: number;
    parent_deal_id?: number;
    split_type: "by_investor" | "by_commitments" | "by_parent_commitments";
  }) => Promise<void>;
  setCommentPopup: (data: CommentPopup | null) => void;
  getMeasureDataErrors: (key: number) => Partial<MeasureDataErrors | undefined>;
  findFirstMeasureDataError: (keys: number[]) => Partial<MeasureDataErrors | undefined>;
  getDealRoundsFromState: (data: { dealId: number; filterDeletedRounds?: boolean }) => MeasureDataRound[] | null;
  manageRounds: (dealId: number) => void;
  getDealRoundsErrors: (dealId: number) => Record<string | number, string[]> | null;
  setImportCommitmentsModal: (data: Pick<Group, "deal_id" | "sub_deal_id">) => void;
  setMeasureDataFilesPickerParam: (data: MeasureDataFilesPickerParam | null) => void;
  dealInvestors: { id: number; name: string }[];
}

export interface MeasureDataFilesPickerParam {
  key: number | null;
  groupId: number | null;
  files: UiMeasureDataFiles | null;
  isTotal: boolean;
  roundId: string | number | null;
}

export const GroupsFormContext = createContext<IGroupsFormContext>({} as IGroupsFormContext);

export const useGroupsForm = () => useContext(GroupsFormContext);
